// Brazilian currency and number formatting utilities

/**
 * Format a number as Brazilian Real currency
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "R$ 1.234,56")
 */
export function formatCurrency(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number in Brazilian style (1.234,56)
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0,00';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Parse a Brazilian formatted currency string to number
 * @param {string} value - The formatted string (e.g., "1.234,56" or "R$ 1.234,56")
 * @returns {number} The parsed number
 */
export function parseCurrency(value) {
  if (!value) return 0;
  
  // Remove currency symbol and whitespace
  let cleaned = value.toString().replace(/[R$\s]/g, '');
  
  // Replace Brazilian format: 1.234,56 -> 1234.56
  cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format a date in Brazilian style (DD/MM/YYYY)
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Get month name in Portuguese
 * @param {number} month - Month number (1-12)
 * @returns {string} Month name in Portuguese
 */
export function getMonthName(month) {
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril',
    'Maio', 'Junho', 'Julho', 'Agosto',
    'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  return months[month - 1] || '';
}

/**
 * Build a list of months BEFORE the given month, most recent first.
 * Used to let the user optionally start a recurring item earlier.
 * @param {{mes:number, ano:number}} current - The current (default start) month
 * @param {number} count - How many previous months to include
 * @returns {Array<{mes:number, ano:number, label:string}>}
 */
export function getPreviousMonthOptions(current, count = 12) {
  const options = [];
  let { mes, ano } = current;
  for (let i = 0; i < count; i++) {
    mes -= 1;
    if (mes < 1) { mes = 12; ano -= 1; }
    options.push({ mes, ano, label: `${getMonthName(mes)} de ${ano}` });
  }
  return options;
}

/**
 * Get short month name in Portuguese
 * @param {number} month - Month number (1-12)
 * @returns {string} Short month name in Portuguese
 */
export function getMonthNameShort(month) {
  const months = [
    'Jan', 'Fev', 'Mar', 'Abr',
    'Mai', 'Jun', 'Jul', 'Ago',
    'Set', 'Out', 'Nov', 'Dez'
  ];
  return months[month - 1] || '';
}

/**
 * Format percentage
 * @param {number} value - The value to format (0.5 = 50%)
 * @returns {string} Formatted percentage string
 */
export function formatPercent(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Calculate percentage
 * @param {number} value - The partial value
 * @param {number} total - The total value
 * @returns {number} Percentage (0-100)
 */
export function calcPercent(value, total) {
  if (!total || total === 0) return 0;
  return (value / total) * 100;
}

// Income types for display
export const INCOME_TYPES = {
  vencimento_bruto: 'Vencimento Bruto',
  '13o_salario': '13º Salário',
  aluguel: 'Aluguel',
  resgate_poupanca: 'Resgate Poupança',
  outros: 'Outros',
};

// Deduction types for display
export const DEDUCTION_TYPES = {
  irrf: 'IRRF',
  inss: 'INSS',
  plano_saude: 'Plano de Saúde',
  plano_saude_custeio: 'Plano de Saúde (Custeio)',
  sindicato: 'Sindicato',
  emprestimo_consignado: 'Empréstimo Consignado',
  vale_transporte: 'Vale Transporte',
  vale_refeicao: 'Vale Refeição',
  pensao_alimenticia: 'Pensão Alimentícia',
  contribuicao_previdencia: 'Previdência Privada',
  cooperativa: 'Cooperativa',
  adiantamento: 'Adiantamento',
  falta_atraso: 'Falta / Atraso',
  imposto_municipal: 'Imposto Municipal (ISS)',
  contribuicao_associativa: 'Contribuição Associativa',
  outros: 'Outros',
};

// Recurring expense categories
export const RECURRING_CATEGORIES = [
  { id: 'aluguel', label: 'Aluguel', icon: 'Home' },
  { id: 'condominio', label: 'Condomínio', icon: 'Building' },
  { id: 'seguro_casa', label: 'Seguro Casa', icon: 'Shield' },
  { id: 'cartao_credito', label: 'Cartão de Crédito', icon: 'CreditCard' },
  { id: 'energia', label: 'Energia', icon: 'Zap' },
  { id: 'celular', label: 'Celular', icon: 'Smartphone' },
  { id: 'tv_internet', label: 'TV/Internet', icon: 'Tv' },
  { id: 'plano_saude', label: 'Plano de Saúde', icon: 'Heart' },
  { id: 'dizimo', label: 'Dízimo', icon: 'Church' },
  { id: 'iptu', label: 'IPTU', icon: 'Building2' },
  { id: 'ir_carne', label: 'IR Carnê Leão', icon: 'FileText' },
  { id: 'poupanca', label: 'Poupança', icon: 'PiggyBank' },
  { id: 'taxa_condo', label: 'Taxa Condomínio', icon: 'Receipt' },
  { id: 'taxa_imobiliaria', label: 'Taxa Imobiliária', icon: 'Home' },
  { id: 'outros', label: 'Outros', icon: 'MoreHorizontal' },
];

// Sporadic expense categories
export const SPORADIC_CATEGORIES = [
  { id: 'faxineira', label: 'Faxineira', icon: 'Sparkles' },
  { id: 'manicure', label: 'Manicure', icon: 'Hand' },
  { id: 'missoes', label: 'Missões/Doações', icon: 'Heart' },
  { id: 'presentes', label: 'Presentes', icon: 'Gift' },
  { id: 'emprestimo', label: 'Empréstimo', icon: 'Landmark' },
  { id: 'saude', label: 'Saúde', icon: 'Stethoscope' },
  { id: 'alimentacao', label: 'Alimentação', icon: 'Utensils' },
  { id: 'transporte', label: 'Transporte', icon: 'Car' },
  { id: 'lazer', label: 'Lazer', icon: 'Coffee' },
  { id: 'outros', label: 'Outras Despesas', icon: 'Receipt' },
];

// Savings goal templates
export const GOAL_TEMPLATES = [
  { id: 'saude', label: 'Reserva para Saúde', icon: 'Heart' },
  { id: 'reforma', label: 'Reforma da Casa', icon: 'Home' },
  { id: 'viagem', label: 'Viagem', icon: 'Plane' },
  { id: 'presentes', label: 'Presentes para Família', icon: 'Gift' },
  { id: 'emergencia', label: 'Reserva de Emergência', icon: 'Shield' },
  { id: 'investimentos', label: 'Investimentos', icon: 'TrendingUp' },
  { id: 'outros', label: 'Outro', icon: 'Target' },
];

// Icon palette for custom (Outros) goals — values are lucide-react icon names
export const GOAL_ICON_PALETTE = [
  'Target', 'Heart', 'Home', 'Plane', 'Gift', 'Shield', 'TrendingUp',
  'Car', 'GraduationCap', 'Briefcase', 'Baby', 'PawPrint', 'BookOpen',
  'ShoppingBag', 'Smartphone', 'Coffee', 'Music', 'Camera', 'Wrench',
  'Star', 'Trophy', 'Building', 'Wallet', 'PiggyBank',
];

// Chart colors
export const CHART_COLORS = [
  '#1B2A4A', // Navy
  '#047857', // Green
  '#D97706', // Amber
  '#DC2626', // Red
  '#0D4F4F', // Teal
  '#7C3AED', // Purple
  '#2563EB', // Blue
  '#EA580C', // Orange
];
